import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * Glass-skin mega menu — soft neumorphic surface, rounded pill rows,
 * floating icon tiles, and one mesh-gradient featured panel.
 */

const Card = ({ children, className = "", style = {}, ...rest }) => (
    <div className={`glass-panel p-5 h-full ${className}`} style={style} {...rest}>
        {children}
    </div>
);

const Eyebrow = ({ children }) => (
    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium mb-3 px-2">
        {children}
    </div>
);

const LinkRow = ({ icon, label, desc, to, onClose }) => (
    <Link
        to={to || ""}
        onClick={onClose}
        className="menu-pill flex items-start gap-3 py-2.5 px-3 group"
        data-testid={`nav-dropdown-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
        {icon && (
            <div className="menu-tile w-9 h-9 shrink-0 rounded-xl grid place-items-center">
                <i className={`${icon} text-[12px] text-slate-700 transition-colors`} />
            </div>
        )}
        <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-900 font-medium leading-tight">{label}</div>
            {desc && <div className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</div>}
        </div>
        <i className="fa-solid fa-arrow-right text-[10px] text-slate-300 self-center opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
    </Link>
);

const SOLUTIONS_MENU = {
    Solutions: [
        {
            eyebrow: "PURPOSE-BUILT AI",
            title: "Three solutions. One AI initiative.",
            desc: "Each focused on a distinct enterprise challenge — all on one secure, scalable AI foundation.",
            items: [
                { label: "Nyra Assist", desc: "Enterprise AI assistant", icon: "fa-solid fa-hands-holding-circle", to: "/solutions/nyra-assist" },
                { label: "Nyra Knowledge", desc: "Knowledge intelligence", icon: "fa-solid fa-book-open", to: "/solutions/nyra-knowledge" },
                { label: "TenderIQ", desc: "AI tender scrutiny", icon: "fa-solid fa-file-signature", to: "/solutions/tenderiq" },
            ],
        },
    ],
    Platform: [
        {
            eyebrow: "CAPABILITIES",
            title: "Under the hood",
            desc: "Six enterprise-grade capabilities behind every NyraAI bot.",
            items: [
                { label: "Conversational AI Engine", icon: "fa-solid fa-brain", to: "/platform" },
                { label: "Agentic AI & Decisions", icon: "fa-solid fa-bolt", to: "/platform" },
                { label: "Omnichannel Engagement", icon: "fa-solid fa-network-wired", to: "/platform" },
                { label: "Live Agent Handoff", icon: "fa-solid fa-user-check", to: "/platform" },
                { label: "Analytics & Insights", icon: "fa-solid fa-chart-column", to: "/platform" },
                { label: "Enterprise Integration", icon: "fa-solid fa-plug", to: "/platform" },
            ],
        },
    ],
    "Use Cases": [
        {
            eyebrow: "BY INDUSTRY",
            title: "Vertical Playbooks",
            desc: "Proven use-case playbooks deployed across every regulated and consumer sector.",
            span: "col-span-12 md:col-span-8",
            items: [
                { label: "Healthcare", desc: "Triage, bookings, refills", icon: "fa-solid fa-stethoscope", to: "/use-cases#healthcare" },
                { label: "E-commerce & Retail", desc: "Sales, support, retention", icon: "fa-solid fa-bag-shopping", to: "/use-cases#ecommerce" },
                { label: "Banking & Finance", desc: "Balances, EMIs, disputes", icon: "fa-solid fa-building-columns", to: "/use-cases#banking" },
                { label: "Insurance", desc: "Quotes, issuance, claims", icon: "fa-solid fa-umbrella", to: "/use-cases#insurance" },
                { label: "Travel & Hospitality", desc: "Bookings & loyalty", icon: "fa-solid fa-plane", to: "/use-cases#travel" },
                { label: "Real Estate", desc: "Lead qualification & visits", icon: "fa-solid fa-house", to: "/use-cases#real-estate" },
                { label: "Education & EdTech", desc: "Admissions & FAQs", icon: "fa-solid fa-graduation-cap", to: "/use-cases#education" },
                { label: "Logistics & Delivery", desc: "Tracking & alerts", icon: "fa-solid fa-truck", to: "/use-cases#logistics" },
            ],
        },
        {
            eyebrow: "CUSTOMER STORIES",
            title: "Real deployments",
            desc: "See how enterprises are already running on Nyra AI.",
            span: "col-span-12 md:col-span-4",
            items: [
                { label: "Distance-Learning Institute", desc: "AI Voice · Distance Education", icon: "fa-solid fa-phone-volume", to: "/customer-stories#mitsde" },
                { label: "Consumer Fintech Platform", desc: "WhatsApp AI · Fintech / Credit", icon: "fa-brands fa-whatsapp", to: "/customer-stories#creditmantri" },
                { label: "All customer stories", desc: "Browse every case study", icon: "fa-solid fa-book-open-reader", to: "/customer-stories" },
            ],
        },
    ],
};

const FEATURED_CARD = {
    Solutions: {
        eyebrow: "ONE AI INITIATIVE",
        title: "Three solutions.\nOne platform.",
        desc: "Every solution ships on the same secure, scalable AI foundation — SOC 2 compliant.",
        ctaLabel: "Book a demo",
        mesh: "",
    },
    Platform: {
        eyebrow: "AGENTIC PLATFORM",
        title: "Intelligence that acts —\nnot just answers.",
        desc: "22+ Indian languages · 85% auto-resolution · Live in under 5 days.",
        ctaLabel: "Request a demo",
        mesh: "",
    },
};

export const MegaMenu = ({ item, onClose, onOpenDemo }) => {
    const cols = SOLUTIONS_MENU[item.label] || [];
    const featured = FEATURED_CARD[item.label];

    return (
        <div className="glass-menu grid grid-cols-12 gap-4 p-4 rounded-[28px]">
            {cols.map((col, i) => {
                const colSpan =
                    col.span ||
                    (featured
                        ? cols.length === 1
                            ? "col-span-12 md:col-span-8"
                            : "col-span-12 md:col-span-4"
                        : cols.length >= 3
                            ? "col-span-12 md:col-span-4"
                            : "col-span-12 md:col-span-6");
                return (
                    <motion.div
                        key={col.eyebrow}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.03 + i * 0.05 }}
                        className={colSpan}
                    >
                        <Card>
                            <Eyebrow>{col.eyebrow}</Eyebrow>
                            {col.title && (
                                <div
                                    className="text-slate-900 leading-tight mb-1 px-2"
                                    style={{ fontFamily: "'Poppins', sans-serif", fontSize: 22 }}
                                >
                                    {col.title}
                                </div>
                            )}
                            {col.desc && (
                                <div className="text-xs text-slate-500 leading-snug mb-4 px-2">{col.desc}</div>
                            )}
                            <div className={col.items.length > 4 ? "grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1" : "space-y-1"}>
                                {col.items.map((it) => (
                                    <LinkRow key={it.label} {...it} onClose={onClose} />
                                ))}
                            </div>
                        </Card>
                    </motion.div>
                );
            })}

            {featured && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="col-span-12 md:col-span-4"
                >
                    <div
                        className="h-full flex flex-col justify-between rounded-2xl bg-white border border-slate-200"
                        style={{ minHeight: 280, padding: 24 }}
                    >
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-800 opacity-80 mb-4">
                                {featured.eyebrow}
                            </div>
                            <div
                                className="text-slate-900 whitespace-pre-line leading-snug"
                                style={{ fontFamily: "'Poppins', sans-serif", fontSize: 26 }}
                            >
                                {featured.title}
                            </div>
                            <div className="text-sm mt-3 text-slate-700 leading-relaxed">
                                {featured.desc}
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                onClose?.();
                                onOpenDemo?.();
                            }}
                            className="pill-dark self-start mt-6"
                            style={{ padding: "10px 18px", fontSize: 13 }}
                            data-testid="megamenu-cta"
                        >
                            {featured.ctaLabel} <i className="fa-solid fa-arrow-right text-[10px]" />
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default MegaMenu;
